import styled from "styled-components"
import tw from "twin.macro"

export interface ButtonProps {
  /**
   * Is this the principal call to action on the page?
   */
  buttonType?: "pill" | ""
  /**
   * Theme decides the color of the button
   */
  buttonTheme?: "alert" | "contrast" | "default" | "muted"
  /**
   * Should the button expand to fill it’s container?
   */
  expand?: boolean
  /**
   * Is the button on a dark background?
   */
  contrast?: boolean
  /**
   * Should the button be unusable
   */
  disabled?: boolean
  /**
   * Adds 2 extra pixels to the right side when there is an icon to make it appear more centered
   */
  hasIcon?: boolean
}

/**
 * A standard button or link for common UI interactions.
 */
const Button = styled("a").withConfig<ButtonProps>({
  shouldForwardProp: (prop, defaultValidatorFn) => {
    return !["contrast", "expand"].includes(prop) && defaultValidatorFn(prop)
  },
})`
  ${tw`inline-block text-sm border-0 focus:outline-none focus:ring`}
  ${(props) =>
    props.buttonType === `pill`
      ? props.contrast
        ? tw`text-action hover:text-action-link bg-white hover:bg-gray-200 rounded-full text-center leading-tight px-3 py-3 min-w-28`
        : {
            alert: tw`text-gray-900  font-normal bg-warning hover:bg-warning-dark rounded-full hover:text-gray-900 text-center leading-tight border border-solid border-warning-dark px-3 py-3 min-w-28`,
            contrast: tw`text-action hover:text-action-link bg-white hover:bg-gray-200 rounded-full text-center leading-tight px-3 py-3 min-w-28`,
            default: tw`text-white font-normal bg-action hover:bg-action-link rounded-full hover:text-white text-center leading-tight px-3 py-3 min-w-28`,
            muted: tw`text-gray-900 font-normal bg-gray-200 hover:bg-gray-300 rounded-full text-center leading-tight px-3 py-3 min-w-28`,
          }[props.buttonTheme ? props.buttonTheme : "default"]
      : tw`text-white p-2 bg-red hover:bg-gray-500 rounded`}
  ${(props) => (props.expand ? tw`w-full` : ``)}
  transition: background-color 150ms ease-in-out;
  ${(props) => (props) => {
    if (!props.buttonTheme || props.buttonTheme === `default`) {
      return `
      &[aria-disabled], &[disabled] {
        background-color: #4D5B73;
        color: rgba(255,255,255,.5);
        cursor: not-allowed;
        pointer-events: none;
      }`
    }
    return null
  }}
  // Adds 2 extra pixels to the right side when there is an icon to make
  // it appear more centered 
  ${(props) =>
    props.hasIcon && props.buttonType === "pill"
      ? `
    padding-right: 14px;
  `
      : ""}
`

export interface ButtonLinkProps {
  /**
   * Is the button on a dark background?
   */
  contrast?: boolean
  /**
   * Determines the text color and prominence in the UI
   */
  colorScheme?: "default" | "muted"
  /**
   * Determines the color of an icon (if present)
   */
  iconColorScheme?: "default" | "light" | "contrast" | "dark"
}

/**
 * A button with the appearance of a link
 */
export const ButtonLink = styled.button<ButtonLinkProps>`
  ${tw`bg-transparent border-0 p-0 m-0 hover:no-underline focus:outline-none focus:ring`}
  text-align: inherit;
  transition: color 150ms ease-in-out;
  ${(props) =>
    ({
      muted:
        props.contrast === true
          ? tw`text-white hover:text-gray-400`
          : tw`text-gray-600 hover:text-gray-700`,
      default:
        props.contrast === true
          ? tw`text-white hover:text-gray-400`
          : tw`text-action-link hover:text-action`,
    }[props.colorScheme ? props.colorScheme : "default"])}
  i {
    margin-right: 0.25em;
    ${(props) =>
      ({
        light: tw`text-cyan`,
        contrast: tw`text-white`,
        dark: tw`text-orange`,
        default: tw`text-action-link`,
      }[props.iconColorScheme ? props.iconColorScheme : "default"])}
  }
`

export default Button
